<template>
  <div class="specs-selector">
    <SpecsSearch
      :groups="groups"
      :is-disabled="checkboxesDisabled"
      @addSearchCategory="addSearchCategory"
    />
    <div class="specs-selector__wrapper">
      <div
        class="specs-selector__groups"
        :class="{ 'specs-selector__groups_has-active': activeGroup }"
        ref="groups"
      >
        <div
          v-for="group in groups"
          :key="group.id"
          :data-group-id="group.id"
          class="specs-selector__group-wrapper"
          :class="{
            'specs-selector__group-wrapper_active':
              activeGroup && group.id === activeGroup.id,
          }"
        >
          <BasicCheckbox
            v-if="allowSelectGroup && !maxSelectedCategoriesCount"
            :minus-when-not-checked="groupHasSelected(group.id)"
            v-model="selectedGroupsCurrent"
            :name="groupFieldName"
            :value="group.id"
            @update:modelValue="
              () => {
                toggleGroup(group);
                onUpdateData();
              }
            "
          />
          <div class="specs-selector__group" @click="setActiveGroup(group)">
            <div class="specs-selector__group-title">
              {{ group.title }}
              <div class="specs-selector__group-subtitle">
                {{ group.translation }}
              </div>
            </div>
            <SvgIcon
              v-if="!allowSelectGroup && groupHasSelected(group.id)"
              class="specs-selector__group-check"
              icon="check"
              size="24"
              title="Категория выбрана"
            />
            <div v-else class="specs-selector__group-check-placeholder" />
            <SvgIcon icon="arrow-forward" size="24" title="Выбрать категорию" />
          </div>
        </div>
      </div>
      <div class="specs-selector__categories-wrapper" ref="categories">
        <div class="specs-selector__categories-title">
          <button
            type="button"
            class="specs-selector__categories-back"
            @click="setActiveGroup(null)"
          >
            <SvgIcon icon="arrow-back" size="24" title="Назад" />
          </button>
          <div class="specs-selector__categories-text">Профессии</div>
        </div>
        <div
          v-for="group in groups"
          :key="group.id"
          class="specs-selector__categories"
          :class="{
            'specs-selector__categories_visible':
              activeGroup && group.id === activeGroup.id,
          }"
        >
          <div
            v-for="category in group.children"
            :key="category.id"
            class="specs-selector__category"
            :data-category-id="category.id"
          >
            <component
              v-show="!textInputOnly(category)"
              :is="useRadio ? 'BasicRadioButton' : 'BasicCheckbox'"
              :id="`category-${category.id}`"
              v-model="selectedCategoriesCurrent"
              class="specs-selector__checkbox"
              :disabled="
                checkboxesDisabled && !categoryIdIsSelected(category.id)
              "
              :name="categoryFieldName"
              :value="category.id"
              @update:modelValue="() => onCategoryChange(category, group)"
            >
              <div class="specs-selector__category-descr">
                <div class="specs-selector__category-label">
                  {{ category.title }}
                  <div class="specs-selector__category-subtitle">
                    {{ category.translation }}
                  </div>
                </div>
              </div>
            </component>
            <div
              v-if="category.hasTextField"
              class="specs-selector__category-other-wrapper"
              :class="
                bem('specs-selector__category-other-wrapper', {
                  fullwidth: textInputOnly(category),
                })
              "
            >
              <TextInput
                v-show="categoryIdIsSelected(category.id)"
                v-model="textFieldsData[category.id]"
                class="specs-selector__category-other"
                :disabled="!categoryIdIsSelected(category.id)"
                :name="`${userTextFieldName.replace('{ID}', category.id)}`"
                placeholder="Название специализации"
                type="text"
                @input="onUpdateData"
              />
              <TextInput
                v-show="!categoryIdIsSelected(category.id)"
                class="specs-selector__category-other"
                disabled
                placeholder="Название специализации"
                type="text"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bem } from 'src/utils/bem.js';

import BasicCheckbox from 'src/components/form/BasicCheckbox/BasicCheckbox.vue';
import BasicRadioButton from 'src/components/form/BasicRadioButton/BasicRadioButton.vue';
import TextInput from 'src/components/form/TextInput/TextInput.vue';
import SvgIcon from 'src/components/icons/SvgIcon/SvgIcon.vue';
import SpecsSearch from 'src/components/SpecsSearch/SpecsSearch.vue';

export default {
  name: 'SpecsSelector',
  components: {
    BasicCheckbox,
    BasicRadioButton,
    TextInput,
    SvgIcon,
    SpecsSearch,
  },
  props: {
    groups: {
      type: Array,
      required: true,
    },
    maxSelectedCategoriesCount: {
      type: Number,
      default: null,
    },
    allowSelectGroup: Boolean,
    selectedGroups: {
      type: Array,
      default: () => [],
    },
    selectedCategories: {
      type: Array,
      default: () => [],
    },
    userCategoriesData: {
      type: Object,
      default: () => ({}),
    },
    groupFieldName: {
      type: String,
      default: 'sg',
    },
    categoryFieldName: {
      type: String,
      default: 'sc',
    },
    userTextFieldName: {
      type: String,
      default: 'text-field-{ID}',
    },
    hasBeenClosed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeGroup: null,
      selectedGroupsCurrent: this.selectedGroups,
      selectedCategoriesCurrent:
        this.maxSelectedCategoriesCount === 1 &&
        this.selectedCategories.length === 1
          ? this.selectedCategories[0]
          : this.selectedCategories,
      textFieldsData: this.userCategoriesData || {},
      groupsValues: {},
    };
  },

  watch: {
    selectedCategories() {
      this.selectedCategoriesCurrent =
        this.maxSelectedCategoriesCount === 1 &&
        this.selectedCategories.length === 1
          ? this.selectedCategories[0]
          : this.selectedCategories;
      this.setAllGroupsStatus();
    },

    hasBeenClosed() {
      if (
        this.selectedGroupsCurrent.length ||
        this.selectedCategoriesCurrent.length
      ) {
        const relatedGroup = this.groups.find(
          (group) => group.id === this.selectedGroupsCurrent[0],
        );

        if (relatedGroup) {
          this.setActiveGroup(relatedGroup);
          this.onCategoryChange(relatedGroup.children[0], relatedGroup);
        }
      } else {
        this.setActiveGroup(null);
      }
    },
  },

  mounted() {
    this.setAllGroupsStatus();
  },

  methods: {
    bem,
    setAllGroupsStatus() {
      this.groups.forEach((group) => {
        this.checkCategory(group.id);
        if (this.groupHasSelected(group.id) && !this.activeGroup) {
          this.setActiveGroup(group);
        }
      });
    },
    onUpdateData() {
      const categoriesArray = Array.isArray(this.selectedCategoriesCurrent)
        ? this.selectedCategoriesCurrent
        : [this.selectedCategoriesCurrent];

      this.$emit('update:data', {
        specs: {
          selectedGroups: this.selectedGroupsCurrent,
          selectedCategories: categoriesArray,
          userCategoriesData: categoriesArray
            .filter((k) => !!this.textFieldsData[k])
            .reduce((a, k) => ({ ...a, [k]: this.textFieldsData[k] }), {}),
        },
      });
    },
    onCategoryChange(_, group) {
      this.checkCategory(group.id);
      this.onUpdateData();
    },
    checkCategory(groupId) {
      const groupIndex = this.selectedGroupsCurrent.indexOf(groupId);
      const allSelected = this.groupHasAllSelected(groupId);
      if (allSelected && groupIndex === -1) {
        this.selectedGroupsCurrent.push(groupId);
      }
      if (!allSelected && groupIndex !== -1) {
        this.selectedGroupsCurrent.splice(groupIndex, 1);
      }
    },
    setActiveGroup(group) {
      this.activeGroup = group;

      if (this.isNotITGroup(group)) {
        const category = group.children[0];
        if (Array.isArray(this.selectedCategoriesCurrent)) {
          this.selectedCategoriesCurrent.push(category.id);
        } else {
          this.selectedCategoriesCurrent = category.id;
        }

        this.onCategoryChange(category, group);
      }
    },
    isNotITGroup(group) {
      return (
        group.children.length === 1 && this.textInputOnly(group.children[0])
      );
    },
    categoryIdIsSelected(categoryId) {
      return Array.isArray(this.selectedCategoriesCurrent)
        ? this.selectedCategoriesCurrent.indexOf(categoryId) !== -1
        : this.selectedCategoriesCurrent === categoryId;
    },
    groupHasSelected(groupId) {
      return (
        this.groups
          .filter((group) => group.id === groupId)[0]
          .children.findIndex((category) =>
            this.categoryIdIsSelected(category.id),
          ) !== -1
      );
    },
    groupHasAllSelected(groupId) {
      return this.groups
        .filter((group) => group.id === groupId)[0]
        .children.every((category) => this.categoryIdIsSelected(category.id));
    },
    toggleGroup(group) {
      const childrenIds = group.children.map((category) => category.id);

      if (this.groupHasAllSelected(group.id)) {
        // uncheck everything if selected
        this.selectedCategoriesCurrent = this.selectedCategoriesCurrent.filter(
          (selectedId) => childrenIds.indexOf(selectedId) === -1,
        );
      } else {
        // check everything if not selected
        this.selectedCategoriesCurrent = Array.from(
          new Set([...this.selectedCategoriesCurrent, ...childrenIds]),
        );
      }
      this.setActiveGroup(group);
    },

    // eslint-disable-next-line complexity
    addSearchCategory(searchCategory) {
      // for radio button
      if (
        !this.selectedCategoriesCurrent ||
        !Array.isArray(this.selectedCategoriesCurrent) ||
        (Array.isArray(this.selectedCategoriesCurrent) &&
          this.maxSelectedCategoriesCount === 1)
      ) {
        this.selectedCategoriesCurrent = searchCategory.id;
        // for checkbox
      } else if (!this.categoryIdIsSelected(searchCategory.id)) {
        this.selectedCategoriesCurrent = [
          ...this.selectedCategoriesCurrent,
          ...[searchCategory.id],
        ];
      }
      this.checkCategory(searchCategory.groupId);
      this.onUpdateData();

      const parentGroup = this.groups.find(
        (group) => group.id === searchCategory.groupId,
      );
      this.setActiveGroup(parentGroup);

      this.scrollToGroupAndCategory(searchCategory);
    },

    scrollToGroupAndCategory(searchCategory) {
      const groupElement = this.$refs.groups.querySelector(
        `[data-group-id="${searchCategory.groupId}"]`,
      );
      if (!groupElement) return;
      groupElement.scrollIntoView({
        block: 'nearest',
        behavior: 'auto',
      });

      this.$nextTick(() => {
        const categoryElement = this.$refs.categories.querySelector(
          `[data-category-id="${searchCategory.id}"]`,
        );
        if (!categoryElement) return;
        categoryElement.scrollIntoView({
          block: 'nearest',
          behavior: 'auto',
        });
      });
    },
    textInputOnly(category) {
      return category.hasTextField && category.hidden;
    },
  },

  computed: {
    useRadio() {
      return this.maxSelectedCategoriesCount === 1;
    },
    checkboxesDisabled() {
      return (
        this.maxSelectedCategoriesCount &&
        this.maxSelectedCategoriesCount > 1 &&
        this.maxSelectedCategoriesCount ===
          this.selectedCategoriesCurrent.length
      );
    },
  },
};
</script>
